import { HTMLAttributes, useEffect, useState } from "react";

import { useNavigate } from "@tanstack/react-router";
import { Loader2, CheckCircle, AlertTriangle } from "lucide-react";
import { AprovedInstallflowFrame } from "./aproved-install-flow-frame";
import ThaloIcon from "@/assets/thalo_icon.svg";

interface AuthProps extends HTMLAttributes<HTMLDivElement> {}

const POWER_CHECK_STEPS = [
  {
    status: "loading" as const,
    text: "Checking power supply...",
    icon: Loader2,
  },
  {
    status: "success" as const,
    text: "Sensor receiving power",
    icon: CheckCircle,
  },
  {
    status: "success" as const,
    text: "Physical connections secure",
    icon: CheckCircle,
  },
  {
    status: "error" as const,
    text: "Sensor not receiving power",
    icon: AlertTriangle,
  },
];

export function FinalCheck({ className }: AuthProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [powerCheckStep, setPowerCheckStep] = useState(0);

  useEffect(() => {
    if (powerCheckStep >= POWER_CHECK_STEPS.length) return;
    const t = setTimeout(() => setPowerCheckStep((s) => s + 1), 1000);
    return () => clearTimeout(t);
  }, [powerCheckStep]);

  useEffect(() => {
    if (powerCheckStep < POWER_CHECK_STEPS.length) return;
    const t = setTimeout(() => {
      navigate({ to: "/installFlow/sensor-installed" });
    }, 2000);
    return () => clearTimeout(t);
  }, [powerCheckStep, navigate]);

  return (
    <AprovedInstallflowFrame>
      <div className="flex flex-col items-center justify-center">
        <div className="flex items-start justify-start w-full mb-6">
          <img src={ThaloIcon} alt="Thalo Icon" className="w-16 h-16"></img>
        </div>

        <h2 className="text-xl font-bold text-black leading-3">
          Almost there. Let’s do a final check...
        </h2>
        <div className="flex flex-col items-center justify-center w-full gap-2 mt-4">
          {POWER_CHECK_STEPS.slice(0, powerCheckStep + 1).map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className="flex items-center gap-2 w-full text-sm">
                {item.status === "loading" ? (
                  <Loader2 className="h-5 w-5 shrink-0 animate-spin text-muted-foreground" />
                ) : item.status === "success" ? (
                  <Icon className="h-5 w-5 shrink-0 text-blue-600" />
                ) : (
                  <Icon className="h-5 w-5 shrink-0 text-red-600" />
                )}
                <span className={item.status === "error" ? "text-red-600" : ""}>
                  {item.text}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </AprovedInstallflowFrame>
  );
}
